import React, { useMemo } from "react";
import { Autocomplete, TextField, Chip } from "@mui/material";

// Multi-select value editor for the `isanyof` filter operator.
// Emits a comma-joined string (e.g. "Egypt,UAE,KSA") so the existing
// FilterDto.value field and the backend's isanyof split on "," both work unchanged.
//
// Props:
//   value: string | undefined      comma-joined selected values
//   onChange: (next: string) => void
//   options: Array<{ value, label }> | Array<string> | (() => Array<...>)
//   label?: string
//   size?: "small" | "medium"
const MultiSelectValueEditor = ({ value, onChange, options, label = "Values", size = "small" }) => {
  const resolvedOptions = useMemo(() => {
    const opts = typeof options === "function" ? options() : options || [];
    return opts.map((o) =>
      typeof o === "object" && o !== null ? { value: String(o.value), label: o.label ?? String(o.value) } : { value: String(o), label: String(o) }
    );
  }, [options]);

  const selected = useMemo(
    () =>
      (value == null || value === "" ? [] : String(value).split(","))
        .map((v) => v.trim())
        .filter(Boolean)
        .map((v) => {
          const match = resolvedOptions.find((o) => o.value === v);
          return { value: v, label: match ? match.label : v };
        }),
    [value, resolvedOptions]
  );

  const handleChange = (_event, nextSelected) => {
    const nextString = (nextSelected || []).map((s) => s.value).join(",");
    onChange(nextString);
  };

  return (
    <Autocomplete
      multiple
      size={size}
      options={resolvedOptions}
      value={selected}
      getOptionLabel={(o) => (o && o.label) || ""}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      onChange={handleChange}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip size="small" label={option.label} {...getTagProps({ index })} />
        ))
      }
      renderInput={(params) => <TextField {...params} label={label} placeholder="Select…" />}
      fullWidth
    />
  );
};

export default MultiSelectValueEditor;
